import { filter, flatten, intersection, isEmpty, pathOr, pluck } from 'ramda';
import * as R from 'remeda';
import { GooglePlace } from '@bridebook/web/lib/search/types';

const TYPES = {
  COUNTRY: ['country'],
  POSTAL_CODE: ['postal_code'],
  CITY: ['locality', 'postal_town'],
  ADMIN: [
    // 'administrative_area_level_5',
    // 'administrative_area_level_4',
    // 'administrative_area_level_3',
    'administrative_area_level_2',
    'administrative_area_level_1',
  ],
};

const NON_STREET_TYPES = [
  ...TYPES.ADMIN,
  ...TYPES.CITY,
  ...TYPES.COUNTRY,
  ...TYPES.POSTAL_CODE,
  /**
   * From Google-Maps API:
   * Indicates a third-order civil entity below the country level.
   * This type indicates a minor civil division. Not all nations
   * exhibit these administrative levels.
   *
   * In DE we use this level for state capitals. To also filter those
   * from the street names (otherwise we'd show "Hamburg, Hamburg")
   * in various places, we have to filter this property.
   *
   * Why not in the `const TYPE` above? This is also used for the admin
   * area property, which indicates usually State and County, but we are
   * not interested in the state capital there, otherwise that would give
   * us "Hamburg, NRW, Hamburg".
   */
  'administrative_area_level_3',
];

const nullIfEmpty = (o: Array<any>) => (isEmpty(o) ? null : o);
const nullIfAllItemsAreEmpty = (o: Array<any>) =>
  isEmpty(o.filter((item) => Boolean(item))) ? null : o;

/**
 * getStreet
 * @param excludeTypes - list of non street types to exlude.
 * assign to street any type that is not listed and. Return long_name.
 * @param addressComponent - address_components from Google API response
 */
export const getStreet = (
  excludeTypes: Array<string>,
  addressComponents: NonNullable<GooglePlace['address_components']>,
) =>
  nullIfEmpty(
    R.pipe(
      addressComponents,
      R.filter((o) => isEmpty(intersection(excludeTypes, o.types))),
      pluck('long_name'),
    ),
  );

/**
 * getByTypes
 * @param types - list of address components types to retrieve.
 * @param addressComponent - address_components from Google API response
 * @param short - return short_name or long_name
 */
const getByTypes = (
  types: Array<string>,
  addressComponent: GooglePlace['address_components'],
  short = false,
) =>
  nullIfEmpty(
    flatten(
      types.map((type) =>
        addressComponent
          ?.map((o) => (o.types.includes(type) ? o[short ? 'short_name' : 'long_name'] : null))
          .filter((item) => item !== null),
      ),
    ),
  );

/**
 * getAdminArea
 * @param types - list of administrative area levels to always include.
 * @param addressComponent - address_components from Google API response
 */
const getAdminArea = (types: Array<string>, addressComponent: GooglePlace['address_components']) =>
  nullIfAllItemsAreEmpty(
    flatten(
      types.map(
        (type) =>
          addressComponent?.find((component) => component?.types && component?.types.includes(type))
            ?.long_name || '',
      ),
    ),
  );

// https://bridebook.atlassian.net/browse/LIVE-8409
export const parseGooglePlace = (place: GooglePlace) => {
  const addressComponents = place.address_components;

  return filter(Boolean, {
    adminArea: getAdminArea(TYPES.ADMIN, addressComponents),
    city: pathOr(null, [0], getByTypes(TYPES.CITY, addressComponents)),
    country: pathOr(null, [0], getByTypes(TYPES.COUNTRY, addressComponents, true)),
    name: pathOr(null, ['name'], place),
    postalCode: pathOr(null, [0], getByTypes(TYPES.POSTAL_CODE, addressComponents)),
    provider: {
      id: pathOr(null, ['place_id'], place),
      type: 'google.places',
    },
    street: getStreet(NON_STREET_TYPES, addressComponents!),
  });
};
